import sessionStorageModule from '../utils/sessionStorageModule.js';
import {
  calculateGrandTotal,
} from '../utils/calculateGrandTotal.js';
import { setSubTotalPrices } from '../utils/setSubTotalPrices.js';
import {
  uiUpdateByNowAndOrderButton,
} from '../payment/uiUpdateByNowAndOrderButton.js';
import { createAndUpdateOrder } from './createAndUpdateOrder.js';
import { eventData } from '../helper/EventData.js';

/**
 * Updates the order quantity of a specific item and recalculates the fees and total amount.
 *
 * @param {string} bucketId - The ID of the bucket whose quantity is to be changed.
 * @param {number} delta - The amount by which the current quantity should be increased or decreased.
 * @param {number} price - The price of the item in the bucket.
 * @param {string} privatApiKey - The private API key for authentication with the API.
 * @param {string} eventBucketId - The ID of the event bucket for which the order is created.
 * @param {Pass|{}} [eventPass] -The event Pass.
 * @returns {Promise<Object>} A promise that resolves to an object containing the updated coupon results and total amount.
 * @throws {Error} An error is thrown if there is a failure during the coupon validation.
 *
 * @example
 * const result = await changeOrderQuantityHandler('bucket1', 1, 20.0, true, 'your-private-api-key', 'event-bucket-id', couponResult);
 * console.log('Updated coupon result:', result.couponResult);
 * console.log('Total amount:', result.totalAmount);
 */
let lastOrderState = null;
let totalSelectedQuantity = 0;  // Variable, um die Gesamtmenge zu verfolgen

export async function changeOrderQuantityHandler(bucketId, delta, price, privatApiKey, eventBucketId, eventPass = {}) {
  const quantityInput = document.getElementById(`quantity-${bucketId}`);
  const minusButton = document.querySelector(`.button--minus[onclick*="${bucketId}"]`);
  const plusButton = document.querySelector(`.button--plus[onclick*="${bucketId}"]`);
  let currentQuantity = parseInt(quantityInput.value, 10);
  const quantityMax = parseInt(quantityInput.getAttribute('max'), 10);
  const userData = sessionStorageModule.getItem('gd-checkout-data');

  // Event-Daten laden
  const eventDataPayLoad = eventData.get();

  // Finde den passenden ticket_bucket oder eine seiner Varianten
  const bucket = findBucketOrVariant(bucketId, eventDataPayLoad.ticket_buckets);

  if (!bucket) {
    console.error(`Kein Ticket Bucket oder Variante für die ID ${bucketId} gefunden.`);
    return;
  }

  const bucketMaxQuantity = bucket.quantity_remaining;  // Verfügbarkeit dieses ticket_buckets
  // console.trace({ bucketMaxQuantity });

  // Berechne die aktuell ausgewählte Gesamtmenge für diesen bucket (inkl. Varianten)
  totalSelectedQuantity = calculateTotalSelectedQuantityForBucket(bucket, eventDataPayLoad.ticket_buckets);

  // Prüfen, ob die neue Gesamtmenge die verfügbare Menge dieses Buckets überschreiten würde
  if ((totalSelectedQuantity + delta) > bucketMaxQuantity) {
    let soldOutInfo = '';

    // Finde die passende Variante anhand der bucketId
    const variant = bucket?.variants?.find(variant => variant?.id === bucketId);

    // Prüfen, ob es sich um "Wheelchair" oder "Rollstuhl" Tickets handelt
    if (bucket?.name?.toLowerCase().includes('wheelchair') || bucket?.name?.toLowerCase().includes('rollstuhl')) {
      soldOutInfo = `We are very sorry. Unfortunately, wheelchair tickets are sold out.`;
    } else if (variant?.name?.toLowerCase() === 'reduced') {
      // Prüfen, ob es eine reduzierte Variante gibt
      soldOutInfo = `We are very sorry. Unfortunately, (reduced) regular tickets are sold out.`;
    }
    else {
      // Standardfall für reguläre Tickets
      soldOutInfo = `We are very sorry. Unfortunately, (reduced) regular tickets are sold out.`;
    }

    // Benachrichtigung anzeigen
    alert(soldOutInfo);
    return;
  }


  // Neue Menge für das aktuelle Item berechnen
  let newQuantity = Math.max(0, Math.min(currentQuantity + delta, quantityMax));

  quantityInput.value = newQuantity;
  totalSelectedQuantity += delta;  // Globale Zählung aktualisieren

  // Bestellung aktualisieren und API aufrufen
  let order = await createAndUpdateOrder(privatApiKey, eventBucketId, userData, eventPass);

  // Preis- und Bestellübersicht aktualisieren
  setSubTotalPrices(order);
  calculateGrandTotal(order);

  // UI-Update nur, wenn sich der Bestellstatus ändert
  if (JSON.stringify(order) !== JSON.stringify(lastOrderState)) {
    lastOrderState = order;
    uiUpdateByNowAndOrderButton(lastOrderState);
  }

  // Buttons deaktivieren oder aktivieren basierend auf der Menge
  updateButtonStates(newQuantity, quantityMax, totalSelectedQuantity, bucketMaxQuantity, minusButton, plusButton);

  return { bucketMaxQuantity };
}

// Hilfsfunktion zur Berechnung der aktuell ausgewählten Gesamtmenge für einen Bucket
function calculateTotalSelectedQuantityForBucket(bucket, allBuckets) {
  let total = 0;

  // Berechne die Menge des Haupt-Buckets
  const mainBucketInput = document.getElementById(`quantity-${bucket.id}`);
  if (mainBucketInput) {
    total += parseInt(mainBucketInput.value, 10);
  }

  // Finde die Varianten und addiere ihre Mengen
  if (bucket.variants && bucket.variants.length > 0) {
    bucket.variants.forEach(variant => {
      const variantInput = document.getElementById(`quantity-${variant.id}`);
      if (variantInput) {
        total += parseInt(variantInput.value, 10);
      }
    });
  }

  return total;
}

// Hilfsfunktion zum Finden eines Buckets oder seiner Variante basierend auf der bucketId
function findBucketOrVariant(bucketId, ticketBuckets) {
  for (let bucket of ticketBuckets) {
    if (bucket.id === bucketId) {
      return bucket;  // Haupt-Bucket gefunden
    }

    if (bucket.variants && bucket.variants.length > 0) {
      for (let variant of bucket.variants) {
        if (variant.id === bucketId) {
          return bucket;  // Variante gefunden, aber gib den Haupt-Bucket zurück
        }
      }
    }
  }

  return null;  // Weder Bucket noch Variante gefunden
}

// Hilfsfunktion zum Aktualisieren des Button-Zustands
function updateButtonStates(currentQuantity, quantityMax, totalSelectedQuantity, bucketMaxQuantity, minusButton, plusButton) {
  // Minus-Button nur aktivieren, wenn die aktuelle Menge größer als 0 ist
  if (currentQuantity > 0) {
    minusButton.disabled = false;
    minusButton.classList.remove('disabled');
  } else {
    minusButton.disabled = true;
    minusButton.classList.add('disabled');
  }

  // Plus-Button deaktivieren, wenn die Maximalmenge für den Bucket erreicht ist
  if (totalSelectedQuantity >= bucketMaxQuantity || currentQuantity >= quantityMax) {
    plusButton.disabled = true;
    plusButton.classList.add('disabled');
  } else {
    plusButton.disabled = false;
    plusButton.classList.remove('disabled');
  }
}






